// src/features/scheduledReports/ScheduledReportsPage.tsx
import { useState, type FormEvent } from 'react';
import { useGuild } from '../../lib/GuildContext';
import {
  useScheduledReports, useCreateReport, useUpdateReport, useDeleteReport, usePreviewReport,
  type ReportType, type ScheduleType, type ScheduledReport, type ReportPreview,
} from './useScheduledReports';
import { formatRelative } from '../../lib/formatTime';
import { ChannelPicker } from '../../components/pickers/ChannelPicker';
import { RolePicker } from '../../components/pickers/RolePicker';
import './ScheduledReportsPage.css';

const SNOWFLAKE_RE = /^\d{17,20}$/;
const TIME_RE = /^([01]\d|2[0-3]):([0-5]\d)$/;

const REPORT_TYPE_LABELS: Record<ReportType, string> = {
  bot_usage: 'Bot usage summary',
  daily_market_summary: 'Daily market summary',
  top_movers: 'Top market movers',
};

const DAY_LABELS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

function scheduleSummary(r: ScheduledReport): string {
  if (r.schedule_type === 'weekly') return `Every ${DAY_LABELS[r.schedule_day_of_week ?? 0]} at ${r.schedule_time} (${r.timezone})`;
  return `Daily at ${r.schedule_time} (${r.timezone})`;
}

function CreateReportForm({ guildId }: { guildId: string }) {
  const [reportType, setReportType] = useState<ReportType>('bot_usage');
  const [scheduleType, setScheduleType] = useState<ScheduleType>('daily');
  const [scheduleTime, setScheduleTime] = useState('09:00');
  const [scheduleDayOfWeek, setScheduleDayOfWeek] = useState(1);
  const [timezone, setTimezone] = useState('UTC');
  const [channelId, setChannelId] = useState('');
  const [mentionRoleId, setMentionRoleId] = useState('');

  const create = useCreateReport(guildId);
  const [justCreated, setJustCreated] = useState(false);

  const channelValid = channelId === '' ? null : SNOWFLAKE_RE.test(channelId);
  const mentionValid = mentionRoleId === '' || SNOWFLAKE_RE.test(mentionRoleId);
  const timeValid = TIME_RE.test(scheduleTime);

  const isValid = channelValid === true && mentionValid && timeValid;

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!isValid) return;

    create.mutate({
      reportType,
      channelId,
      mentionRoleId: mentionRoleId || null,
      scheduleType,
      scheduleTime,
      scheduleDayOfWeek: scheduleType === 'weekly' ? scheduleDayOfWeek : null,
      scheduleIntervalHours: null,
      timezone,
    }, {
      // Deliberately NOT clearing channelId/mentionRoleId here — this
      // used to reset them to '', which made channelValid go from
      // true to null (empty is treated as "not yet filled", not
      // valid), silently re-disabling the button right after a
      // successful create. Fields now stay as-is so a similar report
      // (same channel, different type/schedule) can be created
      // immediately without retyping anything.
      onSuccess: () => {
        setJustCreated(true);
        setTimeout(() => setJustCreated(false), 2500);
      },
    });
  }

  return (
    <form onSubmit={handleSubmit} className="scheduled-reports-form">
      <div className="scheduled-reports-form__row">
        <div className="scheduled-reports-form__field">
          <label htmlFor="reportType">Report type</label>
          <select id="reportType" className="input" value={reportType} onChange={(e) => setReportType(e.target.value as ReportType)}>
            {Object.entries(REPORT_TYPE_LABELS).map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
        </div>
        <div className="scheduled-reports-form__field">
          <label htmlFor="scheduleType">Schedule</label>
          <select id="scheduleType" className="input" value={scheduleType} onChange={(e) => setScheduleType(e.target.value as ScheduleType)}>
            <option value="daily">Daily, at a set time</option>
            <option value="weekly">Weekly, on a set day</option>
          </select>
        </div>
      </div>

      <div className="scheduled-reports-form__row">
        {scheduleType === 'weekly' && (
          <div className="scheduled-reports-form__field">
            <label htmlFor="dayOfWeek">Day</label>
            <select id="dayOfWeek" className="input" value={scheduleDayOfWeek} onChange={(e) => setScheduleDayOfWeek(Number(e.target.value))}>
              {DAY_LABELS.map((day, i) => <option key={day} value={i}>{day}</option>)}
            </select>
          </div>
        )}
        <div className="scheduled-reports-form__field">
          <label htmlFor="scheduleTime">Time (24-hour)</label>
          <input id="scheduleTime" className="input" type="time" value={scheduleTime} onChange={(e) => setScheduleTime(e.target.value)} />
        </div>
        <div className="scheduled-reports-form__field">
          <label htmlFor="timezone">Timezone (IANA)</label>
          <input id="timezone" className="input" type="text" value={timezone} onChange={(e) => setTimezone(e.target.value.trim())} placeholder="e.g. Europe/Vilnius" />
        </div>
      </div>

      <div className="scheduled-reports-form__row">
        <div className="scheduled-reports-form__field">
          <label htmlFor="channelId">Channel ID</label>
          <ChannelPicker guildId={guildId} value={channelId} onChange={setChannelId} placeholder="e.g. 123456789012345678" />
          {channelValid === false && <p className="scheduled-reports-form__error">Must be a Discord snowflake — 17 to 20 digits.</p>}
        </div>
        <div className="scheduled-reports-form__field">
          <label htmlFor="mentionRoleId">Mention role ID (optional)</label>
          <RolePicker guildId={guildId} value={mentionRoleId} onChange={setMentionRoleId} placeholder="e.g. 123456789012345678" />
          {!mentionValid && <p className="scheduled-reports-form__error">Must be a Discord snowflake — 17 to 20 digits.</p>}
        </div>
      </div>

      <button className="button-primary" type="submit" disabled={!isValid || create.isPending}>
        {create.isPending ? 'Creating…' : 'Create scheduled report'}
      </button>
      {create.isError && <span style={{ color: 'var(--negative)', fontSize: 13, marginLeft: '1rem' }}>Couldn&apos;t create the report. Check the fields and try again.</span>}
      {justCreated && <span style={{ color: 'var(--positive)', fontSize: 13, marginLeft: '1rem' }}>Created — change what you need and create another, or scroll down to see it.</span>}
    </form>
  );
}

function PreviewModal({ preview, onClose }: { preview: ReportPreview; onClose: () => void }) {
  return (
    <div className="scheduled-reports-preview-backdrop" onClick={onClose}>
      <div className="scheduled-reports-preview" onClick={(e) => e.stopPropagation()}>
        <div className="card__header">
          <h2 className="card__title">{preview.title}</h2>
          <button className="button-secondary" onClick={onClose}>Close</button>
        </div>
        <div className="card__content">
          <p style={{ color: 'var(--text-secondary)', fontSize: 13 }}>{preview.description}</p>
          {preview.fields.map((f) => (
            <div key={f.name} className="scheduled-reports-preview__field">
              <strong>{f.name}</strong>
              <span>{f.value}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function ReportRow({ guildId, report, index }: { guildId: string; report: ScheduledReport; index: number }) {
  const update = useUpdateReport(guildId);
  const del = useDeleteReport(guildId);
  const preview = usePreviewReport(guildId);
  const [previewData, setPreviewData] = useState<ReportPreview | null>(null);

  return (
    <>
      <tr className={index % 2 === 1 ? 'zebra-row--even' : undefined}>
        <td className="scheduled-reports-table__type">{REPORT_TYPE_LABELS[report.report_type]}</td>
        <td className="scheduled-reports-table__mono">{scheduleSummary(report)}</td>
        <td className="scheduled-reports-table__mono">&lt;#{report.channel_id}&gt;</td>
        <td className="scheduled-reports-table__mono">{formatRelative(report.last_sent_at)}</td>
        <td>
          <button
            className={`toggle${report.enabled ? ' toggle--on' : ''}`}
            role="switch"
            aria-checked={report.enabled}
            aria-label={report.enabled ? 'Disable report' : 'Enable report'}
            onClick={() => update.mutate({ id: report.id, enabled: !report.enabled })}
            disabled={update.isPending}
          >
            <span className="toggle__thumb" />
          </button>
        </td>
        <td style={{ display: 'flex', gap: '0.5rem' }}>
          <button
            className="button-secondary"
            onClick={() => preview.mutate(report.id, { onSuccess: setPreviewData })}
            disabled={preview.isPending}
          >
            {preview.isPending ? 'Loading…' : 'Preview'}
          </button>
          <button className="button-danger" onClick={() => del.mutate(report.id)} disabled={del.isPending}>
            {del.isPending ? 'Removing…' : 'Remove'}
          </button>
        </td>
      </tr>
      {previewData && <PreviewModal preview={previewData} onClose={() => setPreviewData(null)} />}
    </>
  );
}

export function ScheduledReportsPage() {
  const { selectedGuildId } = useGuild();
  const { data: reports, isLoading, error } = useScheduledReports(selectedGuildId);

  return (
    <>
      <div className="page-header">
        <p className="page-header__eyebrow">Recurring summaries</p>
        <h1 className="page-header__title">Scheduled Reports</h1>
      </div>

      <article className="card" style={{ marginBottom: '1.25rem' }}>
        <header className="card__header">
          <div>
            <p className="card__eyebrow">New report</p>
            <h2 className="card__title">Set up a recurring report</h2>
          </div>
        </header>
        <div className="card__content">
          {selectedGuildId && reports && reports.length >= 3 && (
            <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>
              Maximum of 3 scheduled reports per server reached. Delete one below to create another.
            </p>
          )}
          {selectedGuildId && (!reports || reports.length < 3) && <CreateReportForm guildId={selectedGuildId} />}
        </div>
      </article>

      <article className="card">
        <header className="card__header">
          <div>
            <p className="card__eyebrow">{reports?.length ?? 0} reports</p>
            <h2 className="card__title">Active schedules</h2>
          </div>
        </header>
        <div className="card__content">
          {isLoading && <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>Loading…</p>}
          {error && <p style={{ color: 'var(--negative)', fontSize: 13 }}>Couldn&apos;t load reports. Try refreshing.</p>}
          {reports?.length === 0 && <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>No scheduled reports set up yet.</p>}
          {reports && reports.length > 0 && selectedGuildId && (
            <table className="scheduled-reports-table">
              <thead>
                <tr><th>Type</th><th>Schedule</th><th>Channel</th><th>Last sent</th><th>Enabled</th><th></th></tr>
              </thead>
              <tbody>
                {reports.map((report, index) => <ReportRow key={report.id} guildId={selectedGuildId} report={report} index={index} />)}
              </tbody>
            </table>
          )}
        </div>
      </article>
    </>
  );
}
